import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCustomizationStore } from '../store/customizationStore';
import { useLocalizationStore } from '../store/localizationStore';
import { formatCurrency } from '../utils/units';
import { 
  Star, 
  Shield, 
  Droplet, 
  Thermometer, 
  Sun, 
  Sparkles,
  Scale,
  Ruler,
  Zap,
  Info,
  ChevronLeft,
  ChevronRight,
  Check,
  X,
  AlertTriangle,
  Lightbulb,
  Palette,
  Heart,
  ArrowRight,
  Maximize2,
  Minimize2,
  SunMoon
} from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  },
  exit: { opacity: 0 }
};

const cardVariants = {
  hidden: { 
    opacity: 0,
    y: 20,
    scale: 0.95
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  }
};

const specificationCategories = [
  {
    id: 'physical',
    name: 'Physical Properties',
    specs: [
      { id: 'hardness', name: 'Hardness', icon: Shield, unit: 'Mohs' },
      { id: 'waterAbsorption', name: 'Water Absorption', icon: Droplet, unit: '%' },
      { id: 'weight', name: 'Weight', icon: Scale, unit: 'kg/m²' }
    ]
  },
  {
    id: 'resistance',
    name: 'Resistance Properties',
    specs: [
      { id: 'scratchResistance', name: 'Scratch Resistance', icon: Zap, unit: 'Rating' },
      { id: 'heatResistance', name: 'Heat Resistance', icon: Thermometer, unit: '°C' },
      { id: 'uvResistance', name: 'UV Resistance', icon: Sun, unit: 'Rating' }
    ]
  }
];

const lightingConditions = [
  { id: 'morning', name: 'Morning Light', icon: Lightbulb },
  { id: 'afternoon', name: 'Afternoon Light', icon: Sun },
  { id: 'evening', name: 'Evening Light', icon: SunMoon },
  { id: 'studio', name: 'Studio Light', icon: Sparkles }
];

export const MaterialComparison: React.FC = () => {
  const { selectedMaterials, setStep } = useCustomizationStore();
  const { currency } = useLocalizationStore();
  const [activeLighting, setActiveLighting] = useState('studio');
  const [showSpecs, setShowSpecs] = useState(true);
  const [selectedSpec, setSelectedSpec] = useState<string | null>(null);

  const handleBack = () => {
    setStep('material-selection');
  };

  const renderSpecificationValue = (value: number, max: number) => {
    const percentage = (value / max) * 100;
    
    return (
      <div className="relative w-full h-2 bg-gray-200 rounded-full overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          className="absolute h-full bg-gradient-to-r from-gold to-gold-dark"
        />
      </div>
    );
  };

  const renderComparisonResult = (materials: typeof selectedMaterials) => {
    if (materials.length < 2) return null;

    const specs = materials.map(m => m.specifications);
    const bestValues = Object.keys(specs[0] || {}).reduce((acc, key) => {
      acc[key] = Math.max(...specs.map(s => s[key] || 0));
      return acc;
    }, {});

    return (
      <div className="bg-white rounded-xl shadow-lg p-6 mt-8">
        <h3 className="text-xl font-semibold text-gold-dark mb-4">Comparison Summary</h3>
        <div className="space-y-4">
          {Object.entries(bestValues).map(([key, value]) => {
            const bestMaterial = materials.find(m => m.specifications[key] === value);
            if (!bestMaterial) return null;

            return (
              <div key={key} className="flex items-center justify-between">
                <span className="text-black/70 capitalize">Best {key.replace(/([A-Z])/g, ' $1').toLowerCase()}</span>
                <div className="flex items-center gap-2">
                  <span className="font-medium text-gold-dark">{bestMaterial.name}</span>
                  <Check className="w-4 h-4 text-green-500" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      exit="exit"
      className="min-h-screen bg-cream py-16"
    >
      <div className="max-w-7xl mx-auto px-8">
        <button
          onClick={handleBack}
          className="flex items-center text-gold hover:text-gold-dark transition-colors mb-8"
        >
          <ChevronLeft className="w-5 h-5 mr-2" />
          Back to Material Selection
        </button>

        <div className="flex flex-col items-center mb-12">
          <h2 className="text-4xl font-bold text-gold-dark mb-4">
            Material Comparison
          </h2>
          <p className="text-xl text-black/70 max-w-2xl text-center">
            Compare specifications and visualize differences between selected materials
          </p>
        </div>

        {/* Lighting Controls */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
          <h3 className="text-xl font-semibold text-gold-dark mb-4">
            Lighting Conditions
          </h3>
          <div className="grid grid-cols-4 gap-4">
            {lightingConditions.map(condition => {
              const Icon = condition.icon;
              const isActive = activeLighting === condition.id;

              return (
                <motion.button
                  key={condition.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setActiveLighting(condition.id)}
                  className={`p-4 rounded-xl ${
                    isActive 
                      ? 'bg-gold text-white'
                      : 'bg-cream text-gold-dark hover:bg-gold/5'
                  }`}
                >
                  <Icon className="w-6 h-6 mx-auto mb-2" />
                  <span className="block text-sm font-medium">{condition.name}</span>
                </motion.button>
              );
            })}
          </div>
        </div>

        {/* Material Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
          {selectedMaterials.map((material, index) => (
            <motion.div
              key={material.id}
              variants={cardVariants}
              className="bg-white rounded-xl shadow-lg overflow-hidden"
            >
              <div className="relative h-64">
                <img
                  src={material.image}
                  alt={material.name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute bottom-4 left-4">
                  <h3 className="text-xl font-semibold text-white mb-1">
                    {material.name}
                  </h3>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center">
                      {[...Array(5)].map((_, i) => (
                        <Star 
                          key={i}
                          className={`w-4 h-4 ${
                            i < material.popularity / 20
                              ? 'text-gold fill-current'
                              : 'text-white/30'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-white/80 text-sm">
                      {material.popularity}% positive reviews
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Specifications */}
                {specificationCategories.map(category => (
                  <div key={category.id} className="mb-6">
                    <h4 className="font-medium text-gold-dark mb-4">
                      {category.name}
                    </h4>
                    <div className="space-y-4">
                      {category.specs.map(spec => {
                        const Icon = spec.icon;
                        const value = material.specifications?.[spec.id] || 0;

                        return (
                          <div key={spec.id} className="flex items-center gap-4">
                            <div className="w-8 h-8 rounded-lg bg-gold/10 flex items-center justify-center">
                              <Icon className="w-4 h-4 text-gold" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm text-black/70">
                                  {spec.name}
                                </span>
                                <span className="text-sm font-medium text-gold-dark">
                                  {value} {spec.unit}
                                </span>
                              </div>
                              {renderSpecificationValue(value, 10)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Price and Actions */}
                <div className="pt-4 border-t border-gold/10">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <span className="text-sm text-black/70">Price per m²</span>
                      <p className="text-2xl font-bold text-gold-dark">
                        {formatCurrency(material.price, currency)}
                      </p>
                    </div>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-gold text-white px-6 py-3 rounded-lg font-medium hover:bg-gold-dark transition-colors flex items-center gap-2"
                    >
                      Select Material
                      <ArrowRight className="w-5 h-5" />
                    </motion.button>
                  </div>

                  <div className="grid grid-cols-3 gap-4 text-sm text-black/70">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-gold" />
                      <span>10yr warranty</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Truck className="w-4 h-4 text-gold" />
                      <span>Free delivery</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Heart className="w-4 h-4 text-gold" />
                      <span>98% satisfied</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {renderComparisonResult(selectedMaterials)}
      </div>
    </motion.div>
  );
};